import { StepLayout } from "./StepLayout";
import { Button } from "@/components/ui/button";
import { baseCategories, getDosingInfo } from "../data/dosingData";
import { ScanLine, TriangleAlert, User } from "lucide-react";
import { useState } from "react";
import { DosageWithSoliris } from "@/components/TimeLineWithInfusionNew";
import { DosageWithoutSoliris } from "@/components/TimeLineWithOutInfusionNew";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface DosingResultsProps {
  selectedRegion: string;
  selectedWeight: string;
  onNewPatient: () => void;
  onSmPCNavigate: () => void;
}

export const DosingResults = ({
  selectedRegion,
  selectedWeight,
  onNewPatient,
  onSmPCNavigate,
}: DosingResultsProps) => {
  const dosingInfo = getDosingInfo(selectedRegion, selectedWeight);
  const [showTimeLineDose, setShowTimeLineDoes] = useState<boolean>();

  if (!dosingInfo) {
    return (
      <StepLayout
        currentStep={4}
        showImportantNote={true}
        showReference={true}
        selectedRegion={selectedRegion}
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">
            Error: Dosing information not found
          </h1>
        </div>
      </StepLayout>
    );
  }

  const weight =
    baseCategories.find((cat) => cat.value === selectedWeight)?.label ||
    selectedWeight;

  return (
    <StepLayout
      currentStep={4}
      showImportantNote={true}
      showReference={true}
      selectedRegion={selectedRegion}
    >
      <div className="mx-auto mt-10 max-w-6xl">
        {/* Header with Buttons */}
        <div className="mb-12 flex flex-col items-center justify-between lg:flex-row">
          {/* Left Side - Title and Description */}
          <div className="flex-1">
            <h1 className="mb-2 text-xl font-bold text-gray-800 sm:text-4xl">
              Know Your Patient's
            </h1>
            <p className="text-gray-600 sm:text-lg">
              Your patient's recommended dose is....
            </p>
          </div>

          {/* Right Side - Action Buttons */}
          <div className="mt-4 flex w-full flex-col gap-2 lg:w-fit lg:flex-row">
            <Button
              onClick={onNewPatient}
              className="flex w-full items-center space-x-2 rounded-full bg-orange-500 px-6 py-3 font-semibold text-white hover:bg-orange-600 lg:w-fit"
            >
              <User className="h-5 w-5" />
              <span>Another patient</span>
            </Button>
            <Button
              variant="outline"
              onClick={onSmPCNavigate}
              className="flex w-full items-center space-x-2 rounded-full bg-teal-600 px-6 py-3 font-semibold text-white hover:bg-teal-700 hover:text-white lg:w-fit"
            >
              <ScanLine className="h-5 w-5" />
              <span>Scan Full SMPC</span>
            </Button>
          </div>
        </div>
        <Tabs defaultValue="Loading" className="sm:w-[50%]">
          <TabsList className="gap-2">
            <TabsTrigger value="Loading">Loading Dose</TabsTrigger>
            <TabsTrigger value="maintenance">Maintenance Dose</TabsTrigger>
          </TabsList>
          <TabsContent value="Loading" className="">
            <div className="space-y-2">
              <div className="rounded-xl border border-gray-200 bg-white">
                {/* Header */}
                <div className="flex items-center justify-between rounded-t-xl bg-[#E3F7FF] px-6 py-3">
                  <div className="text-sm font-bold text-gray-800">
                    Loading dose (50 mg/mL formulation)
                  </div>
                  <div className="text-base font-bold text-gray-800 sm:text-2xl">
                    300 mg/30 mL
                  </div>
                </div>

                {/* Main Content - Vial and Details */}
                <div className="round flex items-center space-x-8 rounded-b-xl bg-white px-6 py-4">
                  {/* Vial Image */}
                  <div className="flex-shrink-0">
                    <img
                      src="/images/vial300.png"
                      alt="300mg Vial"
                      className="h-40 w-20 object-contain sm:h-56"
                    />
                  </div>

                  {/* Dosing Details */}
                  <div className="flex-1 space-y-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-800">Weight</span>
                      <span className="font-bold text-gray-800">
                        {dosingInfo.weightRange} kg
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-800">
                        Number of ULTOMIRIS<sup>&reg;</sup> vials
                      </span>
                      <span className="font-bold text-gray-800">
                        {dosingInfo.numberOfUltomirisVials300Loading} Vial
                        {dosingInfo.numberOfUltomirisVials300Loading > 1
                          ? "s"
                          : ""}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-800">
                        Minimum infusion time
                      </span>
                      <span className="font-bold text-gray-800">
                        {dosingInfo.minimumInfusionTimeMinLoading} Min
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-800">Total required dose</span>
                      <span className="font-bold text-gray-800">
                        {dosingInfo.numberOfUltomirisVials300Loading * 300} mg
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-800">
                        Number of vials over 3 months
                      </span>
                      <span className="font-bold text-gray-800">
                        {dosingInfo.dosingIntervalByWeeks === 8
                          ? dosingInfo.numberOfUltomirisVials300Loading +
                            dosingInfo.numberOfUltomirisVials300Maintenances * 2
                          : dosingInfo.numberOfUltomirisVials300Loading +
                            3 *
                              dosingInfo.numberOfUltomirisVials300Maintenances}{" "}
                        Vial
                        {(dosingInfo.dosingIntervalByWeeks === 8
                          ? dosingInfo.numberOfUltomirisVials300Loading +
                            dosingInfo.numberOfUltomirisVials300Maintenances * 2
                          : dosingInfo.numberOfUltomirisVials300Loading +
                            3 *
                              dosingInfo.numberOfUltomirisVials300Maintenances) >
                        1
                          ? "s"
                          : ""}{" "}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="maintenance">
            <div className="space-y-2">
              <div className="rounded-xl border border-gray-200 bg-white">
                {/* Header */}
                <div className="flex items-center justify-between rounded-t-xl bg-[#ffd7aa] px-6 py-3">
                  <div className="text-sm font-semibold text-gray-800">
                    Maintenances dose (50 mg/mL formulation)
                  </div>
                  <div className="text-base font-bold text-gray-800 sm:text-2xl">
                    300 mg/30 mL
                  </div>
                </div>

                {/* Main Content - Vial and Details */}
                <div className="round flex items-center space-x-8 rounded-b-xl px-6 py-4">
                  {/* Vial Image */}
                  <div className="mt-auto h-full">
                    <img
                      src="/images/vial300.png"
                      alt="300mg Vial"
                      className="h-40 w-20 object-contain sm:h-56"
                    />
                  </div>

                  {/* Dosing Details */}
                  <div className="flex-1 space-y-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-800">Weight</span>
                      <span className="font-bold text-gray-800">
                        {dosingInfo.weightRange} kg
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-800">
                        Number of ULTOMIRIS<sup>&reg;</sup> vials
                      </span>
                      <span className="font-bold text-gray-800">
                        {dosingInfo.numberOfUltomirisVials300Maintenances} Vial
                        {dosingInfo.numberOfUltomirisVials300Maintenances > 1
                          ? "s"
                          : ""}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-800">
                        Minimum infusion time
                      </span>
                      <span className="font-bold text-gray-800">
                        {dosingInfo.minimumInfusionTimeMinMaintenances} Min
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-800">Total required dose</span>
                      <span className="font-bold text-gray-800">
                        {dosingInfo.numberOfUltomirisVials300Maintenances * 300}{" "}
                        mg
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-800">
                        Number of vials over 3 months
                      </span>
                      <span className="font-bold text-gray-800">
                        {dosingInfo.dosingIntervalByWeeks === 8
                          ? dosingInfo.numberOfUltomirisVials300Maintenances * 2
                          : dosingInfo.numberOfUltomirisVials300Maintenances *
                            3}{" "}
                        Vial
                        {(dosingInfo.dosingIntervalByWeeks === 8
                          ? dosingInfo.numberOfUltomirisVials300Maintenances +
                            dosingInfo.numberOfUltomirisVials300Maintenances
                          : 3 *
                            dosingInfo.numberOfUltomirisVials300Maintenances) >
                        1
                          ? "s"
                          : ""}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <Button
          className="my-5"
          onClick={() => setShowTimeLineDoes((show) => !show)}
        >
          Treatment Journey
        </Button>
        {showTimeLineDose && (
          <div className="my-8 space-y-4">
            <DosageWithSoliris
              weeks={dosingInfo.dosingIntervalByWeeks.toFixed(0)}
              cycles={Number(
                (52 / dosingInfo.dosingIntervalByWeeks).toFixed(0)
              )}
              vialNumber={dosingInfo.numberOfUltomirisVials300Loading}
              maintenanceDose={
                dosingInfo.numberOfUltomirisVials300Maintenances * 300
              }
              loadingDose={dosingInfo.numberOfUltomirisVials300Loading * 300}
              weight={weight}
            />
            <DosageWithoutSoliris
              weeks={dosingInfo.dosingIntervalByWeeks.toFixed(0)}
              cycles={Number(
                (52 / dosingInfo.dosingIntervalByWeeks).toFixed(0)
              )}
              vialNumber={dosingInfo.numberOfUltomirisVials300Maintenances}
              maintenanceDose={
                dosingInfo.numberOfUltomirisVials300Maintenances * 300
              }
              loadingDose={dosingInfo.numberOfUltomirisVials300Loading * 300}
              weight={weight}
            />
          </div>
        )}

        {/* Warning Section */}
        <div className="mt-4 rounded-lg bg-[#F7F7F8] p-3">
          <div className="flex items-start space-x-10">
            <div className="my-auto">
              <TriangleAlert className="h-8 w-8" />
            </div>
            <div className="text-gray-800">
              <ul className="list-disc space-y-1 text-sm">
                <li> Only administer as an intravenous infusion.</li>
                <li>Administer ULTOMIRIS only through a 0.2 micron filter.</li>
                <li> Not to be as an intravenous push or bolus injection.</li>
                <li>
                  Total number of vials is calculated to two cycles of
                  maintenance dosing.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </StepLayout>
  );
};
